'use client';

import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import NextLink from 'next/link';
import { useTranslations } from 'next-intl';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useLandingCTATracking } from '@/features/landing/hooks/useLandingCTATracking';
import { siteConfig } from '@/lib/config/site';

interface InterimCtaProps {
  variant?: 'default' | 'minimal';
  className?: string;
}

export function InterimCtaSection({ variant = 'default', className }: InterimCtaProps) {
  const t = useTranslations('landing.interimCta');
  const { trackCTAClick } = useLandingCTATracking();

  const handleCTAClick = () => {
    trackCTAClick({
      ctaLocation: variant === 'minimal' ? 'interim_minimal' : 'interim_full',
      ctaText: t('cta'),
      ctaType: 'primary',
      section: 'interim_cta',
      destinationUrl: siteConfig.bookingUrl,
    });
  };

  if (variant === 'minimal') {
    return (
      <section className={`py-12 bg-primary/5 border-y border-primary/10 ${className}`}>
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h3 className="text-2xl font-bold text-foreground mb-2">
              {t.rich('title', {
                highlight: (chunks) => <span className="text-primary">{chunks}</span>,
              })}
            </h3>
            <p className="text-muted-foreground">{t('subtitle')}</p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Button
              asChild
              size="lg"
              className="rounded-full font-semibold shadow-lg shadow-primary/20"
            >
              <NextLink
                href={siteConfig.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCTAClick}
              >
                {t('cta')}
                <ArrowRight className="h-4 w-4" />
              </NextLink>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-24 relative overflow-hidden ${className}`}>
      {/* Background Decor */}
      <div className="absolute inset-0 bg-primary/5 -skew-y-2 transform origin-top-left scale-110" />

      <div className="container relative mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto space-y-8"
        >
          <Badge
            variant="outline"
            className="mb-6 px-4 py-1.5 text-sm border-primary/30 bg-primary/10 text-primary"
          >
            <Sparkles className="w-3 h-3 mr-2" />
            {t('badge')}
          </Badge>

          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
            {t.rich('title', {
              highlight: (chunks) => <span className="text-primary">{chunks}</span>,
            })}
          </h2>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('description')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Button
              asChild
              size="lg"
              className="h-14 px-8 text-lg rounded-full shadow-xl shadow-primary/20 hover:scale-105 transition-all"
            >
              <NextLink
                href={siteConfig.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCTAClick}
              >
                {t('cta')}
                <ArrowRight className="h-5 w-5" />
              </NextLink>
            </Button>
            <p className="text-sm text-muted-foreground sm:hidden">{t('guarantee')}</p>
          </div>

          <p className="hidden sm:block text-sm text-muted-foreground/80 italic">
            {t('guarantee')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
